import { useLocation } from 'react-router-dom';
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import esLocale from '@fullcalendar/core/locales/es';
import Select from 'react-select';
import { apiFetch, parseJsonSafe } from '../utils/api';
import { toLocalDateString, toLocalTimeString, toLocalDateTimeString } from '../utils/dateUtils';
import { calendarDate, eventsForDay, eventTimeForDay } from '../utils/agendaDates';
import { BsChevronLeft, BsChevronRight, BsPlusLg, BsCameraVideo } from 'react-icons/bs';
import './AgendaPage.css';
import MeetingReminders, { emptyReminder } from '../components/MeetingReminders';

const hashCode = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
};

// Duración por defecto de un evento nuevo: la mayoría de reuniones son de 30 min.
const DEFAULT_EVENT_MINUTES = 30;

// Suma minutos a un 'HH:mm'. Se limita a 23:59 para no desbordar al día siguiente
// (un input type="time" no puede representarlo y dejaría un fin anterior al inicio).
const addMinutesToTime = (time, minutes) => {
  const [h, m] = String(time || '').split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return '';
  const total = Math.min(h * 60 + m + minutes, 23 * 60 + 59);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

// Los invitados se guardan como un string "a@x.com, b@y.com": los selectores de cliente/contactos
// añaden y quitan emails de esa lista sin pisar los que se hayan escrito a mano.
const parseEmailList = (value) => String(value || '')
  .split(',')
  .map((s) => s.trim())
  .filter(Boolean);

const addEmails = (current, emails) => {
  const list = parseEmailList(current);
  const seen = new Set(list.map((e) => e.toLowerCase()));
  emails.filter(Boolean).forEach((email) => {
    if (seen.has(email.toLowerCase())) return;
    seen.add(email.toLowerCase());
    list.push(email);
  });
  return list.join(', ');
};

const removeEmails = (current, emails) => {
  const drop = new Set(emails.filter(Boolean).map((e) => e.toLowerCase()));
  return parseEmailList(current).filter((e) => !drop.has(e.toLowerCase())).join(', ');
};

// El modal de Bootstrap va por encima del menú de react-select: hay que portarlo al body.
const selectPortalProps = {
  menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
  styles: { menuPortal: (base) => ({ ...base, zIndex: 2000 }) }
};

const AgendaPage = () => {
  const location = useLocation();
  const calendarRef = useRef(null);
  const openedEventRef = useRef(null);
  const eventsRequestRef = useRef(0);
  const initialDate = useRef(toLocalDateString(calendarDate(new URLSearchParams(location.search).get('date')) || new Date())).current;
  const token = localStorage.getItem('token');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [modalEvent, setModalEvent] = useState(null);
  const [form, setForm] = useState({ summary: '', description: '', start: '', end: '', attendees: '' });
  const [editError, setEditError] = useState('');
  const [calendarColors, setCalendarColors] = useState({});
  const [currentRange, setCurrentRange] = useState({ start: '', end: '' });
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth <= 768 : false));
  const [activeView, setActiveView] = useState('dayGridMonth');
  const [calendarTitle, setCalendarTitle] = useState('');
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const mobileMonth = isMobile && activeView === 'dayGridMonth';
  const selectedEvents = useMemo(() => eventsForDay(events, selectedDate), [events, selectedDate]);
  const selectedDateLabel = calendarDate(selectedDate)?.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' }) || '';

  const [createModal, setCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ date: '', start: '', end: '', summary: '', description: '', attendees: '', addConference: false, sendNotify: false });
  const [createError, setCreateError] = useState('');
  const [createReminder, setCreateReminder] = useState(emptyReminder);
  const [editReminder, setEditReminder] = useState(emptyReminder);
  const [reminderHistory, setReminderHistory] = useState([]);
  const [reminderLoading, setReminderLoading] = useState(false);
  const [reminderLoaded, setReminderLoaded] = useState(false);
  const [reminderError, setReminderError] = useState('');
  const reminderRequestRef = useRef(0);

  // Selectores de invitados: cliente (solo los que tienen email) y sus contactos con email.
  const [clientQuery, setClientQuery] = useState('');
  const [clientOptions, setClientOptions] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [attendeeClient, setAttendeeClient] = useState(null);
  const [contactOptions, setContactOptions] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [attendeeContacts, setAttendeeContacts] = useState([]);

  const openCreateModalWithDate = useCallback((startDate, endDate, viewType) => {
    const isMonthView = viewType === 'dayGridMonth';
    const startTime = toLocalTimeString(startDate);
    const effectiveStart = (isMonthView && startTime === '00:00') ? '09:00' : startTime;
    // Si no hay selección de fin (vista de mes, o clic simple), 30 min por defecto.
    // Si el usuario arrastró un rango en la vista de horas, se respeta ese fin.
    const effectiveEnd = endDate && !(isMonthView && startTime === '00:00')
      ? toLocalTimeString(endDate)
      : addMinutesToTime(effectiveStart, DEFAULT_EVENT_MINUTES);

    setCreateError('');
    setCreateReminder(emptyReminder());
    setCreateForm({
      date: toLocalDateString(startDate),
      start: effectiveStart,
      end: effectiveEnd,
      summary: '',
      description: '',
      attendees: '',
      addConference: false,
      sendNotify: false
    });
    setClientQuery('');
    setAttendeeClient(null);
    setContactOptions([]);
    setAttendeeContacts([]);
    setCreateModal(true);
  }, []);

  // Busca clientes solo mientras el bloque de Meet está abierto. Sin texto trae la primera página;
  // el listado se filtra a los que tienen email, que son los únicos invitables.
  useEffect(() => {
    if (!createModal || !createForm.addConference) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setClientsLoading(true);
      try {
        const params = new URLSearchParams({
          page: '1',
          pageSize: '100',
          hasEmail: 'true',
          sort: 'razonSocial',
          order: 'asc'
        });
        if (clientQuery.trim()) params.set('search', clientQuery.trim());
        const res = await apiFetch(`/api/clients?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await parseJsonSafe(res);
        if (cancelled) return;
        const options = (data?.data || [])
          .filter((c) => c.email && String(c.email).trim())
          .map((c) => ({
            value: c.id,
            email: String(c.email).trim(),
            label: `${c.razon_social || 'Sin nombre'} · ${String(c.email).trim()}`
          }));
        setClientOptions(options);
      } catch {
        if (!cancelled) setClientOptions([]);
      } finally {
        if (!cancelled) setClientsLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [createModal, createForm.addConference, clientQuery, token]);

  const handleAttendeeClientChange = async (option) => {
    const previousEmails = [
      ...(attendeeClient ? [attendeeClient.email] : []),
      ...attendeeContacts.map((c) => c.email)
    ];
    setAttendeeClient(option || null);
    setAttendeeContacts([]);
    setContactOptions([]);
    setCreateForm((p) => ({
      ...p,
      attendees: addEmails(removeEmails(p.attendees, previousEmails), option ? [option.email] : [])
    }));
    if (!option) return;

    setContactsLoading(true);
    try {
      const res = await apiFetch(`/api/clients/${option.value}/contacts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await parseJsonSafe(res);
      const options = (Array.isArray(data) ? data : [])
        .filter((c) => c.email && String(c.email).trim())
        .map((c) => ({
          value: c.id,
          email: String(c.email).trim(),
          label: `${c.nombre || 'Contacto'}${c.cargo ? ` (${c.cargo})` : ''} · ${String(c.email).trim()}`
        }));
      setContactOptions(options);
    } catch {
      setContactOptions([]);
    } finally {
      setContactsLoading(false);
    }
  };

  const handleAttendeeContactsChange = (options) => {
    const selected = options || [];
    const removed = attendeeContacts
      .filter((prev) => !selected.some((o) => o.value === prev.value))
      .map((o) => o.email);
    setAttendeeContacts(selected);
    setCreateForm((p) => ({
      ...p,
      attendees: addEmails(removeEmails(p.attendees, removed), selected.map((o) => o.email))
    }));
  };

  const handleUnauthorized = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('userEmail');
    window.location.href = '/login';
  };

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Escape key and backdrop click to close modals
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (createModal) setCreateModal(false);
        if (modalEvent) setModalEvent(null);
      }
    };
    if (createModal || modalEvent) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [createModal, modalEvent]);

  const loadEvents = async (startStr, endStr) => {
    const requestId = ++eventsRequestRef.current;
    setLoading(true);
    setError('');
    try {
      const res = await apiFetch(`/api/calendar/events?from=${encodeURIComponent(startStr)}&to=${encodeURIComponent(endStr)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await parseJsonSafe(res);
      if (requestId !== eventsRequestRef.current) return;
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error cargando eventos');
      const palette = ['#4f46e5','#0ea5e9','#22c55e','#f97316','#ec4899','#a855f7','#14b8a6'];
      const uniqueCalendars = Array.from(new Set((data || []).map((e) => e.calendarId).filter(Boolean)));
      const colorMap = { ...calendarColors };
      uniqueCalendars.forEach((calId) => {
        if (!colorMap[calId]) colorMap[calId] = palette[hashCode(calId) % palette.length];
      });
      setCalendarColors(colorMap);
      setEvents(
        (data || []).map((e) => ({
          id: e.id,
          title: e.summary || '(sin título)',
          start: e.start,
          end: e.end,
          description: e.description || '',
          backgroundColor: e.calendarId ? colorMap[e.calendarId] : undefined,
          borderColor: e.calendarId ? colorMap[e.calendarId] : undefined,
          extendedProps: {
            hangoutLink: e.hangoutLink,
            calendarId: e.calendarId,
            description: e.description,
            attendees: e.attendees || []
          }
        }))
      );
    } catch (err) {
      if (requestId === eventsRequestRef.current) setError(err.message);
    } finally {
      if (requestId === eventsRequestRef.current) setLoading(false);
    }
  };

  const openModal = (ev) => {
    setModalEvent(ev);
    setEditError('');
    setEditReminder(emptyReminder());
    setReminderHistory([]);
    setReminderError('');
    setReminderLoaded(false);
    const request = ++reminderRequestRef.current;
    const calId = ev?.extendedProps?.calendarId;
    if (calId) {
      setReminderLoading(true);
      apiFetch(`/api/calendar/events/${encodeURIComponent(ev.id)}/reminders?calendarId=${encodeURIComponent(calId)}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(async (r) => { const data = await parseJsonSafe(r); if (!r.ok) throw new Error(data.message || 'No se pudo cargar el recordatorio'); return data; })
        .then((data) => { if (request !== reminderRequestRef.current) return; setEditReminder({ ...emptyReminder(), ...data.config }); setReminderHistory(data.deliveries || []); setReminderError(data.lastError || ''); setReminderLoaded(true); })
        .catch((e) => { if (request === reminderRequestRef.current) setReminderError(e.message); })
        .finally(() => { if (request === reminderRequestRef.current) setReminderLoading(false); });
    }
    const attendees = (ev?.extendedProps?.attendees || [])
      .map((a) => a?.email)
      .filter((email) => email)
      .join(', ');
    setForm({
      summary: ev?.title || '',
      description: ev?.extendedProps?.description || ev?.description || '',
      start: ev?.start ? toLocalDateTimeString(ev.start) : '',
      end: ev?.end ? toLocalDateTimeString(ev.end) : '',
      attendees
    });
  };

  useEffect(() => {
    const eventId = new URLSearchParams(location.search).get('eventId');
    if (!eventId || openedEventRef.current === eventId) return;
    const event = calendarRef.current?.getApi().getEventById(eventId);
    if (event) { openedEventRef.current = eventId; openModal(event); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, location.search]);

  const handleDelete = async () => {
    if (!modalEvent || saving) return;
    if (!window.confirm(`¿Eliminar "${modalEvent.title || 'este evento'}"? Esta acción no se puede deshacer.`)) return;
    setSaving(true);
    setEditError('');
    try {
      const res = await apiFetch(`/api/calendar/events/${modalEvent.id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ calendarId: modalEvent.extendedProps?.calendarId })
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo eliminar');
      setModalEvent(null);
      const refStart = currentRange.start || form.start || new Date().toISOString();
      const refEnd = currentRange.end || form.end || new Date().toISOString();
      await loadEvents(refStart, refEnd);
    } catch (err) {
      setEditError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    if (saving) return;
    if (!createForm.date || !createForm.start || !createForm.end) {
      setCreateError('Fecha y horas son obligatorias');
      return;
    }
    setSaving(true);
    setCreateError('');
    try {
      const res = await apiFetch('/api/calendar/events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          date: createForm.date,
          time: createForm.start,
          endTime: createForm.end,
          summary: createForm.summary,
          description: createForm.description,
          attendees: createForm.addConference ? createForm.attendees : '',
          addConference: createForm.addConference,
          sendNotify: createForm.sendNotify,
          reminders: createForm.addConference ? createReminder : { enabled: false }
        })
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo crear');
      setCreateModal(false);
      const refStart = currentRange.start || `${createForm.date}T${createForm.start}:00`;
      const refEnd = currentRange.end || `${createForm.date}T${createForm.end}:00`;
      await loadEvents(refStart, refEnd);
      if (data.reminderError || data.reminder?.warning) setError(data.reminderError || data.reminder.warning);
    } catch (err) {
      setCreateError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveModal = async () => {
    if (!modalEvent || saving) return;
    const calId = modalEvent.extendedProps?.calendarId;
    if (!calId) {
      setEditError('Este evento no tiene calendarId, no se puede actualizar. Vuelve a cargar la agenda.');
      return;
    }
    setSaving(true);
    setEditError('');
    try {
      const res = await apiFetch(`/api/calendar/events/${modalEvent.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          summary: form.summary,
          description: form.description,
          start: form.start || null,
          end: form.end || null,
          calendarId: calId,
          attendees: form.attendees,
          ...(reminderLoaded ? { reminders: editReminder } : {})
        })
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo actualizar');
      setModalEvent(null);
      const refStart = currentRange.start || form.start || new Date().toISOString();
      const refEnd = currentRange.end || form.end || new Date().toISOString();
      await loadEvents(refStart, refEnd);
      if (data.reminderError || data.reminder?.warning) setError(data.reminderError || data.reminder.warning);
    } catch (err) {
      setEditError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Al cambiar la hora de inicio, la de fin se recoloca SIEMPRE a +30 min (duración
  // habitual de una reunión). Si hace falta otra duración, se edita el fin a mano después.
  const handleCreateStartChange = useCallback((newStart) => {
    setCreateForm((p) => ({
      ...p,
      start: newStart,
      end: addMinutesToTime(newStart, DEFAULT_EVENT_MINUTES) || p.end
    }));
  }, []);

  return (
    <div className={`agenda-page${mobileMonth ? ' agenda-mobile-month' : ''}`}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2 className="mb-0">Agenda</h2>
          <div className="text-muted small">Sincronizada con Google Calendar</div>
        </div>
        {loading && <span className="badge bg-info text-dark">Cargando...</span>}
      </div>
      {error && <div className="alert alert-danger">{error}</div>}
      <div className="card shadow-sm agenda-calendar-card">
        <div className="card-body">
          {isMobile && <div className="agenda-mobile-toolbar">
            <div className="agenda-month-nav">
              <button type="button" className="btn btn-light" aria-label={activeView === 'dayGridMonth' ? 'Mes anterior' : 'Día anterior'} onClick={() => calendarRef.current?.getApi().prev()}><BsChevronLeft /></button>
              <h3 className="agenda-month-title" aria-live="polite">{calendarTitle}</h3>
              <button type="button" className="btn btn-light" aria-label={activeView === 'dayGridMonth' ? 'Mes siguiente' : 'Día siguiente'} onClick={() => calendarRef.current?.getApi().next()}><BsChevronRight /></button>
            </div>
            <div className="agenda-mobile-actions">
              <div className="btn-group" aria-label="Vista de agenda">
                <button type="button" className={`btn ${activeView === 'dayGridMonth' ? 'btn-primary' : 'btn-outline-primary'}`} aria-pressed={activeView === 'dayGridMonth'} onClick={() => calendarRef.current?.getApi().changeView('dayGridMonth', selectedDate)}>Mes</button>
                <button type="button" className={`btn ${activeView === 'timeGridDay' ? 'btn-primary' : 'btn-outline-primary'}`} aria-pressed={activeView === 'timeGridDay'} onClick={() => calendarRef.current?.getApi().changeView('timeGridDay', selectedDate)}>Día</button>
              </div>
              <button type="button" className="btn btn-outline-secondary" onClick={() => { setSelectedDate(toLocalDateString(new Date())); calendarRef.current?.getApi().today(); }}>Hoy</button>
            </div>
          </div>}
          <FullCalendar
            ref={calendarRef}
            initialDate={initialDate}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            locale={esLocale}
            initialView="dayGridMonth"
            headerToolbar={isMobile ? false : {
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay'
            }}
            height={isMobile ? 'auto' : '80vh'}
            contentHeight={isMobile ? 'auto' : undefined}
            expandRows={!isMobile}
            fixedWeekCount={!isMobile}
            events={events}
            eventDisplay={mobileMonth ? 'none' : 'auto'}
            dayHeaderContent={mobileMonth ? (arg) => <span title={arg.date.toLocaleDateString('es-ES', { weekday: 'long' })}>{['D', 'L', 'M', 'X', 'J', 'V', 'S'][arg.date.getDay()]}</span> : undefined}
            dayCellContent={mobileMonth ? (arg) => {
              const dateKey = toLocalDateString(arg.date);
              const items = loading || error ? [] : eventsForDay(events, dateKey);
              const label = arg.date.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
              return <button type="button" className={`agenda-day-button${arg.isOther ? ' agenda-other-month' : ''}`} aria-pressed={dateKey === selectedDate} aria-current={arg.isToday ? 'date' : undefined} aria-label={`${label}${loading || error ? '' : `, ${items.length} ${items.length === 1 ? 'cita' : 'citas'}`}`} onClick={() => setSelectedDate(dateKey)}>
                <span className="agenda-day-number">{arg.date.getDate()}</span>
                <span className="agenda-day-markers" aria-hidden="true">{items.slice(0, 3).map((event, index) => <span key={index} className="agenda-day-marker" style={{ background: event.backgroundColor || '#2e9be0' }} />)}{items.length > 3 && <span>+</span>}</span>
              </button>;
            } : undefined}
            selectable={!mobileMonth}
            select={(info) => {
              openCreateModalWithDate(info.start, info.end, info.view?.type);
            }}
            dateClick={(info) => {
              if (mobileMonth) { setSelectedDate(toLocalDateString(info.date)); return; }
              openCreateModalWithDate(info.date, null, info.view?.type);
            }}
            eventClick={(info) => {
              openModal(info.event);
            }}
            datesSet={(arg) => {
              const start = arg.start.toISOString();
              const end = arg.end.toISOString();
              setActiveView(arg.view.type);
              setCalendarTitle(arg.view.title);
              setSelectedDate((previous) => {
                if (arg.view.type === 'timeGridDay') return toLocalDateString(arg.view.currentStart);
                const selected = calendarDate(previous);
                if (selected && selected >= arg.view.currentStart && selected < arg.view.currentEnd) return previous;
                const today = new Date();
                return toLocalDateString(today >= arg.view.currentStart && today < arg.view.currentEnd ? today : arg.view.currentStart);
              });
              setCurrentRange({ start, end });
              loadEvents(start, end);
            }}
          />
        </div>
      </div>

      {mobileMonth && <section className="agenda-selected-day" aria-labelledby="agenda-selected-title" aria-busy={loading}>
        <div className="agenda-selected-heading">
          <div><h3 id="agenda-selected-title">{selectedDateLabel}</h3><div className="small text-muted">{loading ? 'Cargando citas…' : error ? 'No se pudieron cargar las citas' : `${selectedEvents.length} ${selectedEvents.length === 1 ? 'cita' : 'citas'}`}</div></div>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => openCreateModalWithDate(calendarDate(selectedDate), null, 'dayGridMonth')}><BsPlusLg aria-hidden="true" /> Nueva cita</button>
        </div>
        <div className="agenda-day-events">
          {!loading && !error && selectedEvents.map((event) => <button type="button" className="agenda-event-card" key={`${event.extendedProps?.calendarId || ''}:${event.id}`} style={{ '--agenda-event-color': event.backgroundColor || '#2e9be0' }} onClick={() => openModal({ ...event, start: calendarDate(event.start), end: calendarDate(event.end) })}>
            <span className="agenda-event-main"><span className="agenda-event-title">{event.title}</span><span className="agenda-event-time">{eventTimeForDay(event, selectedDate)}{event.extendedProps?.hangoutLink && <> · <BsCameraVideo aria-hidden="true" /> Google Meet</>}</span></span><BsChevronRight aria-hidden="true" />
          </button>)}
          {!loading && !error && !selectedEvents.length && <div className="agenda-day-empty">No hay citas para este día.</div>}
        </div>
      </section>}

      {/* Modal editar evento */}
      {modalEvent && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.4)' }} onClick={(e) => { if (e.target === e.currentTarget) setModalEvent(null); }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Editar evento</h5>
                <button className="btn-close" onClick={() => setModalEvent(null)}></button>
              </div>
              <div className="modal-body">
                {editError && <div className="alert alert-danger py-2">{editError}</div>}
                <div className="mb-2">
                  <label className="form-label">Título</label>
                  <input className="form-control" value={form.summary} onChange={(e) => setForm((p) => ({ ...p, summary: e.target.value }))} />
                </div>
                <div className="mb-2">
                  <label className="form-label">Descripción</label>
                  <textarea className="form-control" rows="3" value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} />
                </div>
                <div className="mb-2">
                  <label className="form-label">Invitados (emails separados por coma)</label>
                  <input className="form-control" value={form.attendees} onChange={(e) => setForm((p) => ({ ...p, attendees: e.target.value }))} />
                </div>
                <div className="mb-2">
                  <label className="form-label">Inicio</label>
                  <input className="form-control" type="datetime-local" value={form.start} onChange={(e) => {
                    const newStart = e.target.value;
                    setForm((p) => {
                      const updated = { ...p, start: newStart };
                      if (p.start && p.end && newStart) {
                        const diff = new Date(p.end).getTime() - new Date(p.start).getTime();
                        if (diff > 0) {
                          const newEnd = new Date(new Date(newStart).getTime() + diff);
                          const off = newEnd.getTimezoneOffset() * 60000;
                          updated.end = new Date(newEnd.getTime() - off).toISOString().slice(0, 16);
                        }
                      }
                      return updated;
                    });
                  }} />
                </div>
                <div className="mb-2">
                  <label className="form-label">Fin</label>
                  <input className="form-control" type="datetime-local" value={form.end} onChange={(e) => setForm((p) => ({ ...p, end: e.target.value }))} />
                </div>
                {modalEvent?.extendedProps?.hangoutLink && (
                  <div className="mb-2">
                    <label className="form-label">Enlace Meet</label>
                    <div className="d-flex align-items-center gap-2">
                      <input className="form-control" value={modalEvent.extendedProps.hangoutLink} readOnly />
                      <a className="btn btn-outline-primary" href={modalEvent.extendedProps.hangoutLink} target="_blank" rel="noreferrer">Abrir</a>
                    </div>
                  </div>
                )}
                {(modalEvent?.extendedProps?.hangoutLink || editReminder.enabled || reminderHistory.length > 0) && <MeetingReminders value={editReminder} onChange={setEditReminder} attendees={form.attendees} token={token} disabled={saving || !reminderLoaded} loading={reminderLoading} error={reminderError} history={reminderHistory} />}
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setModalEvent(null)}>Cerrar</button>
                <button className="btn btn-outline-danger me-auto" onClick={handleDelete} disabled={saving}>
                  {saving ? 'Eliminando...' : 'Eliminar'}
                </button>
                <button className="btn btn-primary" onClick={handleSaveModal} disabled={saving}>
                  {saving ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal crear evento */}
      {createModal && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.4)' }} onClick={(e) => { if (e.target === e.currentTarget) setCreateModal(false); }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Crear evento</h5>
                <button className="btn-close" onClick={() => setCreateModal(false)}></button>
              </div>
              <div className="modal-body">
                {createError && <div className="alert alert-danger py-2">{createError}</div>}
                <div className="mb-2">
                  <label className="form-label">Título</label>
                  <input className="form-control" value={createForm.summary} onChange={(e) => setCreateForm((p) => ({ ...p, summary: e.target.value }))} autoFocus />
                </div>
                <div className="mb-2">
                  <label className="form-label">Fecha</label>
                  <input className="form-control" type="date" value={createForm.date} onChange={(e) => setCreateForm((p) => ({ ...p, date: e.target.value }))} />
                </div>
                <div className="row mb-2">
                  <div className="col-6">
                    <label className="form-label">Hora inicio</label>
                    <input className="form-control" type="time" value={createForm.start} onChange={(e) => handleCreateStartChange(e.target.value)} />
                  </div>
                  <div className="col-6">
                    <label className="form-label">Hora fin</label>
                    <input className="form-control" type="time" value={createForm.end} onChange={(e) => setCreateForm((p) => ({ ...p, end: e.target.value }))} />
                  </div>
                </div>
                <div className="mb-2">
                  <label className="form-label">Descripción</label>
                  <textarea className="form-control" rows="2" value={createForm.description} onChange={(e) => setCreateForm((p) => ({ ...p, description: e.target.value }))} />
                </div>
                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="addConference"
                    checked={createForm.addConference}
                    onChange={(e) => setCreateForm((p) => ({ ...p, addConference: e.target.checked, sendNotify: e.target.checked ? p.sendNotify : false }))}
                  />
                  <label className="form-check-label" htmlFor="addConference">
                    Añadir reunión de Google Meet
                  </label>
                </div>
                {createForm.addConference && (
                  <>
                    <div className="mb-2">
                      <label className="form-label">Cliente</label>
                      <Select
                        options={clientOptions}
                        value={attendeeClient}
                        onChange={handleAttendeeClientChange}
                        onInputChange={(value, meta) => {
                          if (meta.action === 'input-change') setClientQuery(value);
                        }}
                        isLoading={clientsLoading}
                        isClearable
                        placeholder="Buscar cliente con email…"
                        loadingMessage={() => 'Buscando…'}
                        noOptionsMessage={() => 'Ningún cliente con email'}
                        {...selectPortalProps}
                      />
                    </div>
                    {attendeeClient && (
                      <div className="mb-2">
                        <label className="form-label">Contactos del cliente</label>
                        <Select
                          isMulti
                          options={contactOptions}
                          value={attendeeContacts}
                          onChange={handleAttendeeContactsChange}
                          isLoading={contactsLoading}
                          placeholder={contactsLoading ? 'Cargando contactos…' : 'Añadir contactos a la invitación…'}
                          noOptionsMessage={() => 'Este cliente no tiene contactos con email'}
                          {...selectPortalProps}
                        />
                      </div>
                    )}
                    <div className="mb-2">
                      <label className="form-label">Invitados (emails separados por coma)</label>
                      <input className="form-control" value={createForm.attendees} onChange={(e) => setCreateForm((p) => ({ ...p, attendees: e.target.value }))} />
                      <div className="form-text">El cliente y los contactos que selecciones se añaden aquí. También puedes escribir emails libremente.</div>
                    </div>
                    <div className="form-check mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="sendNotify"
                        checked={createForm.sendNotify}
                        onChange={(e) => setCreateForm((p) => ({ ...p, sendNotify: e.target.checked }))}
                      />
                      <label className="form-check-label" htmlFor="sendNotify">
                        Enviar invitación ahora por email
                      </label>
                    </div>
                    <MeetingReminders value={createReminder} onChange={setCreateReminder} attendees={createForm.attendees} token={token} disabled={saving} />
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setCreateModal(false)}>Cerrar</button>
                <button className="btn btn-primary" onClick={handleCreate} disabled={saving}>
                  {saving ? 'Creando...' : 'Crear'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgendaPage;
